import React, { useState, useEffect, useCallback } from 'react';
import API from '../utils/api';
import toast from 'react-hot-toast';
import { PageLoader, SectionHeader, ActionBtn, Pagination } from '../components/common/UI';
import { toShamsiWithDay } from '../utils/dateHelpers';
import { ALERT_TYPES } from '../utils/statusHelpers';
import { MdNotificationsOff, MdDoneAll } from '../utils/icons';

export default function Alerts() {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1); // پیج‌بندی (صفحه‌بندی سمت کلاینت)

  const load = useCallback(async () => {
    try {
      const { data } = await API.get('/alerts');
      setAlerts(data);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const markRead = async (id) => {
    try {
      await API.put(`/alerts/${id}/read`);
      setAlerts(prev => prev.filter(a => a._id !== id));
    } catch { toast.error('خطا'); }
  };

  const markAllRead = async () => {
    try {
      await API.put('/alerts/read-all');
      toast.success('همه هشدارها خوانده شد');
      setAlerts([]);
    } catch { toast.error('خطا'); }
  };

  const priorityColors = {
    Low: 'border-slate-200 bg-slate-100',
    Medium: 'border-yellow-200 bg-yellow-100',
    High: 'border-orange-200 bg-orange-100',
    Critical: 'border-red-200 bg-red-100',
  };
  const priorityLabels = { Low: 'کم', Medium: 'متوسط', High: 'زیاد', Critical: 'بحرانی' };
  const priorityBadge = {
    Low: 'bg-slate-100 text-slate-700',
    Medium: 'bg-yellow-100 text-yellow-700',
    High: 'bg-orange-100 text-orange-700',
    Critical: 'bg-red-100 text-red-700'
  };

  if (loading) return <PageLoader />;

  const PAGE_SIZE = 10;
  const totalPages = Math.max(1, Math.ceil(alerts.length / PAGE_SIZE));
  const paged = alerts.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <div className="space-y-5 fade-in">
      <SectionHeader
        title="هشدارها"
        subtitle={`${alerts.length} هشدار خوانده نشده`}
        actions={alerts.length > 0 && (
          <ActionBtn onClick={markAllRead} icon={<MdDoneAll size={16} />} label="همه را خواندم" variant="ghost" />
        )}
      />

      {alerts.length === 0 ? (
        <div className="glass-card p-16 text-center">
          <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
            <MdNotificationsOff size={36} className="text-green-600" />
          </div>
          <h3 className="text-gray-800 font-semibold text-lg mb-2">هیچ هشدار جدیدی ندارید</h3>
          <p className="text-gray-500 text-sm">سیستم همه چیز را به خوبی دنبال می‌کند</p>
        </div>
      ) : (
        <div className="space-y-3">
          {paged.map(alert => {
            const typeInfo = ALERT_TYPES[alert.type] || { icon: '🔔', label: alert.type, color: 'text-gray-500' };
            return (
              <div key={alert._id} className={`glass-card p-4 border ${priorityColors[alert.priority]} transition-all hover:scale-[1.01]`}>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-gray-50 flex items-center justify-center text-2xl flex-shrink-0">
                    {typeInfo.icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-3 mb-1">
                      <h3 className="text-gray-800 font-semibold">{alert.title}</h3>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span className={`badge ${priorityBadge[alert.priority]}`}>{priorityLabels[alert.priority]}</span>
                        <button
                          onClick={() => markRead(alert._id)}
                          className="p-1.5 text-green-600 hover:text-green-700 hover:bg-green-100 rounded-lg transition-all"
                          title="علامت به عنوان خوانده شده"
                        >
                          <MdDoneAll size={15} />
                        </button>
                      </div>
                    </div>
                    <p className="text-gray-600 text-sm mb-2">{alert.message}</p>
                    <div className="flex items-center gap-3 text-xs text-gray-600">
                      <span className={typeInfo.color}>{typeInfo.label}</span>
                      {alert.relatedProject && <span>📁 {alert.relatedProject.name}</span>}
                      <span>{toShamsiWithDay(alert.createdAt)}</span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
          <Pagination page={page} totalPages={totalPages} onChange={setPage} />
        </div>
      )}
    </div>
  );
}
